import { useEffect, useState } from "react";

/**
 * Twitch/YouTube iframe embed. Twitch requires `parent` query param.
 */
export function StreamEmbed({ platform, channel, url, className = "" }) {
    const [parent, setParent] = useState("");
    useEffect(() => {
        setParent(window.location.hostname);
    }, []);

    if (!platform && !url) return null;

    let src = "";
    if (platform === "twitch" && channel) {
        src = parent ? `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${encodeURIComponent(parent)}&muted=true` : "";
    } else if (platform === "youtube") {
        // url expected to be embed url or video id
        const videoId = url?.split("v=").pop()?.split("&")[0] || url;
        src = `https://www.youtube.com/embed/${videoId}?autoplay=0`;
    } else if (url) {
        src = url;
    }

    if (!src) {
        return (
            <div className={`relative aspect-video border border-white/10 bg-[#050505] flex items-center justify-center ${className}`}>
                <div className="text-center">
                    <div className="overline text-white/40 mb-2">Stream loading</div>
                    <div className="font-mono text-xs text-white/25">Connecting to broadcast…</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative aspect-video border border-white/10 bg-black overflow-hidden ${className}`} data-testid="stream-embed">
            <iframe
                src={src}
                title="Live stream"
                allowFullScreen
                frameBorder="0"
                scrolling="no"
                className="absolute inset-0 w-full h-full"
            />
        </div>
    );
}

/**
 * Compact standings table with "advance zone" highlight.
 */
export function Standings({ rows, advances, compact = false }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="border border-white/10 clip-frame-sm p-8 text-center">
                <div className="overline text-white/40 mb-2">No standings yet</div>
                <div className="font-mono text-xs text-white/50">Points appear once the first match ends.</div>
            </div>
        );
    }
    return (
        <div className="border border-white/10 clip-frame bg-[#0A0A0A] overflow-hidden" data-testid="standings-table">
            {/* header */}
            <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 bg-[#050505]">
                <div className="col-span-1 overline text-white/40">#</div>
                <div className={`${compact ? "col-span-7" : "col-span-6"} overline text-white/40`}>Squad</div>
                {!compact && <div className="col-span-2 overline text-white/40">MP</div>}
                <div className={`${compact ? "col-span-4" : "col-span-3"} overline text-white/40 text-right`}>Points</div>
            </div>
            {rows.map((r) => {
                const inZone = r.in_advance_zone;
                return (
                    <div key={r.team_id || r.rank}
                        data-testid={`standings-row-${r.rank}`}
                        className={`grid grid-cols-12 items-center px-5 py-3 border-b border-white/5 last:border-b-0 relative
                            ${inZone ? "bg-gradient-to-r from-volt/10 via-transparent to-transparent" : ""}`}>
                        {inZone && (
                            <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-volt" />
                        )}
                        <div className={`col-span-1 font-display font-black text-lg ${r.rank === 1 ? "text-volt" : inZone ? "text-white" : "text-white/40"}`}>
                            {String(r.rank).padStart(2, "0")}
                        </div>
                        <div className={`${compact ? "col-span-7" : "col-span-6"} min-w-0`}>
                            <div className="font-display font-extrabold text-sm leading-tight truncate">{r.team_name || "—"}</div>
                            {r.team_tag && <div className="font-mono text-[10px] text-volt tracking-[0.24em] mt-0.5">{r.team_tag}</div>}
                        </div>
                        {!compact && <div className="col-span-2 font-mono text-xs text-white/60">{r.matches_played}</div>}
                        <div className={`${compact ? "col-span-4" : "col-span-3"} text-right`}>
                            <div className={`font-display font-black text-lg ${inZone ? "text-volt" : "text-white/80"}`}>{r.points.toLocaleString()}</div>
                        </div>
                    </div>
                );
            })}
            {/* Advance zone marker */}
            {advances && rows.length > advances && (
                <div className="relative px-5 py-2 bg-[#050505] border-t border-signal/30">
                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-signal">
                        ─ Cutline · Top {advances} advance ─
                    </div>
                </div>
            )}
        </div>
    );
}
